// Complete authentication flow routing for Heapster ID
// Implements PRD Section 5.2 onboarding flow and authentication routes

import { createBrowserRouter, Navigate } from 'react-router-dom';
import App from './App';
import { PublicRoute } from './components/auth/PublicRoute';
import {
  ProtectedRoute,
  VerifiedRoute,
  AdminRoute,
} from './components/auth/ProtectedRoute';
import { SignInPage } from './pages/SignInPage';
import { SignUpPage } from './pages/SignUpPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { AuthVerifyCompletePage } from './pages/AuthVerifyCompletePage';
import { OidcContinuePage } from './pages/OidcContinuePage';
import { OidcLogoutPage } from './pages/OidcLogoutPage';
import { CompleteProfilePage } from './pages/CompleteProfilePage';
import { DashboardPage } from './pages/DashboardPage';
import { ProfilePage } from './pages/ProfilePage';
import { SettingsPage } from './pages/SettingsPage';
import { NotFoundPage, StubPage } from './pages/PlaceholderPages';
import { ContactPage } from './pages/ContactPage';
import { UserListPage } from './pages/admin/UserListPage';
import { BadgeManagementPage } from './pages/admin/BadgeManagementPage';
import { RedeemGroupListPage } from './pages/admin/RedeemGroupListPage';
import { AgreementListPage } from './pages/admin/AgreementListPage';
import { AgreementDetailPage } from './pages/admin/AgreementDetailPage';
import { EntitlementCatalogPage } from './pages/admin/EntitlementCatalogPage';
import { SubscriptionsPage } from './pages/admin/SubscriptionsPage';
import { PricingPage } from './pages/PricingPage';
import { SubscriptionSuccessPage } from './pages/SubscriptionSuccessPage';
import { SubscriptionCancelPage } from './pages/SubscriptionCancelPage';
import { SubscriptionManagePage } from './pages/SubscriptionManagePage';
import { RedeemPage } from './pages/RedeemPage';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      // Public routes (redirect authenticated users)
      {
        index: true,
        element: (
          <PublicRoute>
            <SignInPage />
          </PublicRoute>
        ),
      },
      {
        path: 'signin',
        element: (
          <PublicRoute>
            <SignInPage />
          </PublicRoute>
        ),
      },
      {
        path: 'signup',
        element: (
          <PublicRoute>
            <SignUpPage />
          </PublicRoute>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        ),
      },

      // Authentication flow routes (require authentication but handle different states)
      {
        path: 'verify-email',
        element: (
          <ProtectedRoute>
            <VerifyEmailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'auth/verify-complete',
        element: <AuthVerifyCompletePage />,
      },
      // OIDC relying-party sign-in continuation. Unguarded because the page
      // does its own redirect: with no session it sends the user to sign-in
      // (sign-up for `prompt=create`) with `returnTo` back to itself.
      //
      // It has no email-verification gate, by design (#1832): the backend
      // reports `email_verified` as a claim and each relying party applies its
      // own policy. Do not wrap it in a verified-email guard or add a
      // `/verify-email` detour. That reverses #1832, and an account with no
      // email address cannot complete `/verify-email`: the page shows
      // `user.email`, and resending throws 'User has no email address'
      // (`sendVerificationEmail` in AuthContext). The page offers sign-out and
      // account deletion, but no way to a verified state.
      {
        path: 'oidc/continue',
        element: <OidcContinuePage />,
      },
      // OIDC RP-initiated logout (#1901). Unguarded for a stronger reason than
      // `oidc/continue`: `ProtectedRoute` renders a redirect the moment `user`
      // goes null, which here is the moment the page does its job, and
      // `PublicRoute` would bounce the signed-in user it exists to sign out.
      //
      // Reachable with no `request_id`, which is what makes it the linkable
      // sign-out URL the partner guide points testers at — `<issuer>/logout`
      // cannot be, because it answers 503 wherever OIDC is unconfigured.
      {
        path: 'oidc/logout',
        element: <OidcLogoutPage />,
      },
      {
        path: 'complete-profile',
        element: (
          <ProtectedRoute requireEmailVerified={true}>
            <CompleteProfilePage />
          </ProtectedRoute>
        ),
      },

      // Protected routes (require full authentication and verification)
      {
        path: 'dashboard',
        element: (
          <VerifiedRoute>
            <DashboardPage />
          </VerifiedRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <VerifiedRoute>
            <ProfilePage />
          </VerifiedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <VerifiedRoute>
            <SettingsPage />
          </VerifiedRoute>
        ),
      },

      // Admin routes (require admin role)
      {
        path: 'admin',
        element: (
          <AdminRoute>
            <UserListPage />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/badges',
        element: (
          <AdminRoute>
            <BadgeManagementPage />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/redeem-groups',
        element: (
          <AdminRoute>
            <RedeemGroupListPage />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/agreements',
        element: (
          <AdminRoute>
            <AgreementListPage />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/agreements/:slug',
        element: (
          <AdminRoute>
            <AgreementDetailPage />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/entitlements',
        element: (
          <AdminRoute>
            <EntitlementCatalogPage />
          </AdminRoute>
        ),
      },
      {
        // Superseded by the cross-provider console (#1701). Kept as a redirect
        // rather than deleted: this path has been the admin's bookmark for
        // external subscriptions since #1558, and a 404 is a worse answer than
        // the page that replaced it.
        //
        // Spelled out rather than referenced through `routes` — that object is
        // declared below this one, so naming it here is a use-before-assign.
        // Its `adminExternalSubscriptions` entry is gone: nothing links to this
        // path any more, and a constant kept for a redirect that does not use
        // it is just a dead export.
        path: 'admin/external-subscriptions',
        element: <Navigate to="/admin/subscriptions" replace />,
      },
      {
        path: 'admin/subscriptions',
        element: (
          <AdminRoute>
            <SubscriptionsPage />
          </AdminRoute>
        ),
      },

      // Subscription routes (public pricing, protected management)
      {
        path: 'pricing',
        element: <PricingPage />,
      },
      {
        path: 'subscription/success',
        element: (
          <ProtectedRoute>
            <SubscriptionSuccessPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'subscription/cancel',
        element: (
          <ProtectedRoute>
            <SubscriptionCancelPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'subscriptions',
        element: (
          <VerifiedRoute>
            <SubscriptionManagePage />
          </VerifiedRoute>
        ),
      },
      // Redirect old subscription/manage URL to new subscriptions URL for backward compatibility
      {
        path: 'subscription/manage',
        element: (
          <VerifiedRoute>
            <SubscriptionManagePage />
          </VerifiedRoute>
        ),
      },

      // Redemption routes (require full authentication and verification)
      {
        path: 'redeem',
        element: (
          <VerifiedRoute>
            <RedeemPage />
          </VerifiedRoute>
        ),
      },
      {
        path: 'redeem/:code',
        element: (
          <VerifiedRoute>
            <RedeemPage />
          </VerifiedRoute>
        ),
      },

      // Static/legal pages (public access)
      {
        path: 'terms',
        element: <StubPage title="Terms of Service" />,
      },
      {
        path: 'privacy',
        element: <StubPage title="Privacy Policy" />,
      },
      {
        path: 'contact',
        element: <ContactPage />,
      },

      // 404 catch-all
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);

// Export route paths for consistent navigation throughout the app
export const routes = {
  // Public routes
  home: '/',
  signUp: '/signup',

  // Authentication flow
  verifyEmail: '/verify-email',
  completeProfile: '/complete-profile',
  oidcContinue: '/oidc/continue',

  // Protected user routes
  dashboard: '/dashboard',
  profile: '/profile',
  settings: '/settings',

  // Subscription routes
  pricing: '/pricing',
  subscriptionSuccess: '/subscription/success',
  subscriptionCancel: '/subscription/cancel',
  subscriptions: '/subscriptions',

  // Admin routes
  admin: '/admin',
  adminBadges: '/admin/badges',
  adminRedeemGroups: '/admin/redeem-groups',
  adminAgreements: '/admin/agreements',
  adminEntitlements: '/admin/entitlements',
  adminSubscriptions: '/admin/subscriptions',

  // Redemption routes
  redeem: '/redeem',
  redeemWithCode: (code: string) => `/redeem/${code}` as const,

  // Static pages
  terms: '/terms',
  privacy: '/privacy',
  contact: '/contact',
} as const;

// Route metadata for navigation and breadcrumbs
export const routeMetadata = {
  [routes.home]: { title: 'Sign In', public: true },
  [routes.signUp]: { title: 'Create Account', public: true },
  [routes.verifyEmail]: { title: 'Verify Email', protected: true },
  [routes.oidcContinue]: { title: 'Continue Sign-In', public: true },
  [routes.completeProfile]: { title: 'Complete Profile', protected: true },
  [routes.dashboard]: { title: 'Dashboard', protected: true, verified: true },
  [routes.profile]: { title: 'Profile', protected: true, verified: true },
  [routes.settings]: { title: 'Settings', protected: true, verified: true },
  [routes.pricing]: { title: 'Pricing', public: true },
  [routes.subscriptionSuccess]: {
    title: 'Subscription Success',
    protected: true,
  },
  [routes.subscriptionCancel]: {
    title: 'Subscription Cancelled',
    protected: true,
  },
  [routes.subscriptions]: {
    title: 'Manage Subscription',
    protected: true,
    verified: true,
  },
  [routes.admin]: { title: 'Admin Console', protected: true, admin: true },
  [routes.adminBadges]: {
    title: 'Badge Management',
    protected: true,
    admin: true,
  },
  [routes.adminRedeemGroups]: {
    title: 'Redeem Groups',
    protected: true,
    admin: true,
  },
  [routes.adminAgreements]: {
    title: 'Agreements',
    protected: true,
    admin: true,
  },
  [routes.adminEntitlements]: {
    title: 'Entitlements',
    protected: true,
    admin: true,
  },
  [routes.adminSubscriptions]: {
    title: 'Subscriptions',
    protected: true,
    admin: true,
  },
  [routes.redeem]: {
    title: 'Redeem Code',
    protected: true,
    verified: true,
  },
} as const;
